/**
 * Semantic design-token derivation for pharmacy-aware theming.
 * Pure color math only — no UI/business logic. Given a pharmacy's raw
 * brand colors and the active light/dark theme, produces a safe,
 * WCAG-AA-aware set of CSS custom properties that components bind to
 * instead of reading raw colors.
 */

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
export const DEFAULT_PRIMARY = '#047857';
export const DEFAULT_PRIMARY_DARK = '#10b981';
export const DEFAULT_ACCENT = '#0369a1';
export const DEFAULT_ACCENT_DARK = '#38bdf8';

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.padEnd(6, '0').slice(0, 6);
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex([r, g, b]) {
  return `#${[r, g, b].map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('')}`;
}
function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}
function hslToRgb([h, s, l]) {
  h /= 360; s /= 100; l /= 100;
  if (s === 0) { const v = l * 255; return [v, v, v]; }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue2rgb = (pp, qq, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return pp + (qq - pp) * 6 * tt;
    if (tt < 1 / 2) return qq;
    if (tt < 2 / 3) return pp + (qq - pp) * (2 / 3 - tt) * 6;
    return pp;
  };
  return [hue2rgb(p, q, h + 1 / 3) * 255, hue2rgb(p, q, h) * 255, hue2rgb(p, q, h - 1 / 3) * 255];
}
function channelLuminance(c) {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
}
function relativeLuminance([r, g, b]) {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}
export function contrastRatio(hexA, hexB) {
  const la = relativeLuminance(hexToRgb(hexA));
  const lb = relativeLuminance(hexToRgb(hexB));
  const [lighter, darker] = la > lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

const safeHex = (hex, fallback) => (typeof hex === 'string' && HEX_RE.test(hex) ? hex : fallback);

/** Pick black or near-black vs white, whichever reads better on this color. */
export function onColor(hex) {
  const white = '#ffffff';
  const ink = '#111827';
  return contrastRatio(hex, white) >= contrastRatio(hex, ink) ? white : ink;
}

/**
 * Keep the pharmacy's hue/chroma but nudge lightness only as far as needed
 * so the color stays legible as a UI fill: visible against `bgHex` (the
 * page/surface it will sit on — light or dark) and able to pair with either
 * white or dark text at >=4.5:1. The original logo asset is never touched —
 * this only adjusts derived UI tokens.
 */
export function ensureUiSafe(hex, bgHex = '#ffffff') {
  const [h, s] = rgbToHsl(hexToRgb(hex));
  let l = rgbToHsl(hexToRgb(hex))[2];
  let candidate = hex;
  const bgIsLight = relativeLuminance(hexToRgb(bgHex)) > 0.4;
  const step = bgIsLight ? -4 : 4;
  let guard = 0;
  while (contrastRatio(candidate, bgHex) < 3 && guard < 40) {
    l = clamp(l + step, 0, 100);
    candidate = rgbToHex(hslToRgb([h, s, l]));
    guard += 1;
  }
  guard = 0;
  while (Math.max(contrastRatio(candidate, '#ffffff'), contrastRatio(candidate, '#111827')) < 4.5 && guard < 40) {
    l = l > 50 ? clamp(l - 4, 0, 100) : clamp(l + 4, 0, 100);
    candidate = rgbToHex(hslToRgb([h, s, l]));
    guard += 1;
  }
  return candidate;
}

/** Tonal container derived from a safe brand color, plus matching readable text — light or dark aware. */
function deriveContainer(safeColor, mode) {
  const [h, s, l] = rgbToHsl(hexToRgb(safeColor));
  if (mode === 'dark') {
    const container = rgbToHex(hslToRgb([h, clamp(s * 0.45, 0, 100), 20]));
    let onL = clamp(l + 35, 55, 92);
    let onContainer = rgbToHex(hslToRgb([h, clamp(s, 30, 85), onL]));
    let guard = 0;
    while (contrastRatio(onContainer, container) < 4.5 && onL < 97 && guard < 20) {
      onL += 3;
      onContainer = rgbToHex(hslToRgb([h, clamp(s, 30, 85), onL]));
      guard += 1;
    }
    return { container, onContainer };
  }
  const container = rgbToHex(hslToRgb([h, clamp(s * 0.55, 0, 100), 94]));
  let onL = clamp(l * 0.42, 8, 28);
  let onContainer = rgbToHex(hslToRgb([h, clamp(s, 35, 85), onL]));
  let guard = 0;
  while (contrastRatio(onContainer, container) < 4.5 && onL > 4 && guard < 20) {
    onL -= 3;
    onContainer = rgbToHex(hslToRgb([h, clamp(s, 35, 85), onL]));
    guard += 1;
  }
  return { container, onContainer };
}

/**
 * Fixed meanings — never recolored to match a pharmacy's brand. Darker in
 * light mode (so text reads on a pale pastel container) and lighter in
 * dark mode (so text reads on a near-black surface); each ships its own
 * tonal container so status pills/badges have a safe background too.
 */
const STATUS_BY_MODE = {
  light: {
    '--status-success': '#15803d',
    '--status-success-container': '#dcfce7',
    '--status-warning': '#b45309',
    '--status-warning-container': '#fef3c7',
    '--status-danger': '#b91c1c',
    '--status-danger-container': '#fee2e2',
    '--status-info': '#0369a1',
    '--status-info-container': '#e0f2fe',
  },
  dark: {
    '--status-success': '#4ade80',
    '--status-success-container': 'rgba(74, 222, 128, 0.16)',
    '--status-warning': '#fbbf24',
    '--status-warning-container': 'rgba(251, 191, 36, 0.16)',
    '--status-danger': '#f87171',
    '--status-danger-container': 'rgba(248, 113, 113, 0.16)',
    '--status-info': '#38bdf8',
    '--status-info-container': 'rgba(56, 189, 248, 0.16)',
  },
};

/** Theme-only neutrals (surfaces, text, glass, elevation) — independent of any pharmacy's brand color. */
const NEUTRALS = {
  light: {
    '--background': '#f4f7f5',
    '--surface': '#ffffff',
    '--surface-subtle': '#f8fafc',
    '--surface-raised': '#ffffff',
    '--surface-glass': 'rgba(255, 255, 255, 0.72)',
    '--surface-glass-border': 'rgba(255, 255, 255, 0.6)',
    '--text-primary': '#0f172a',
    '--text-secondary': '#475569',
    '--text-disabled': '#6b7280',
    '--border-subtle': '#e2e8f0',
    '--border-strong': '#cbd5e1',
    '--scrim': 'rgba(15, 23, 42, 0.45)',
    '--shadow-card': '0 8px 30px rgba(15, 23, 42, 0.06)',
    '--shadow-card-hover': '0 16px 40px rgba(15, 23, 42, 0.1)',
    '--shadow-dock': '0 -10px 30px rgba(15, 23, 42, 0.06)',
  },
  dark: {
    '--background': '#0a1310',
    '--surface': '#121d19',
    '--surface-subtle': '#16221d',
    '--surface-raised': '#152019',
    '--surface-glass': 'rgba(16, 26, 22, 0.68)',
    '--surface-glass-border': 'rgba(255, 255, 255, 0.08)',
    '--text-primary': '#f1f5f4',
    '--text-secondary': '#9fb0a8',
    '--text-disabled': '#8a9a93',
    '--border-subtle': '#203029',
    '--border-strong': '#2b3c34',
    '--scrim': 'rgba(0, 0, 0, 0.6)',
    '--shadow-card': '0 8px 30px rgba(0, 0, 0, 0.35)',
    '--shadow-card-hover': '0 18px 44px rgba(0, 0, 0, 0.5)',
    '--shadow-dock': '0 -10px 30px rgba(0, 0, 0, 0.4)',
  },
};

/** In dark mode, a pastel brand color (e.g. a saved near-white accent) can
 * look washed out as a solid fill/gradient stop. Cap its lightness a touch
 * so it still reads as a vivid tonal color rather than fading to white. */
function capDarkLightness(hex, maxL = 78) {
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  if (l <= maxL) return hex;
  return rgbToHex(hslToRgb([h, Math.max(s, 35), maxL]));
}

/**
 * Build the full semantic token set for a pharmacy in a given theme mode.
 * Returns a plain object suitable for a React `style` prop (CSS custom
 * properties). Includes `--portal-*` aliases so existing CSS already bound
 * to those names keeps working while new code adopts the semantic names.
 */
export function buildPharmacyTokens(pharmacy, mode = 'light') {
  const neutrals = NEUTRALS[mode] || NEUTRALS.light;
  const defaultPrimary = mode === 'dark' ? DEFAULT_PRIMARY_DARK : DEFAULT_PRIMARY;
  const defaultAccent = mode === 'dark' ? DEFAULT_ACCENT_DARK : DEFAULT_ACCENT;
  let primarySafe = ensureUiSafe(safeHex(pharmacy?.primary_color, defaultPrimary), neutrals['--surface']);
  // If a pharmacy customized its primary color but never set an accent,
  // fall the accent back to that same primary (monochrome) instead of the
  // app's independent default accent — avoids a mismatched two-tone look
  // (e.g. a purple-branded pharmacy getting a purple-to-green gradient).
  const accentFallback = HEX_RE.test(pharmacy?.primary_color || '') ? pharmacy.primary_color : defaultAccent;
  let accentSafe = ensureUiSafe(safeHex(pharmacy?.accent_color, accentFallback), neutrals['--surface']);
  if (mode === 'dark') {
    primarySafe = capDarkLightness(primarySafe);
    accentSafe = capDarkLightness(accentSafe);
  }
  const onPrimary = onColor(primarySafe);
  const onAccent = onColor(accentSafe);
  const { container: primaryContainer, onContainer: onPrimaryContainer } = deriveContainer(primarySafe, mode);

  return {
    '--brand-primary': primarySafe,
    '--brand-on-primary': onPrimary,
    '--brand-primary-container': primaryContainer,
    '--brand-on-primary-container': onPrimaryContainer,
    '--brand-accent': accentSafe,
    '--brand-on-accent': onAccent,
    ...neutrals,
    '--focus-ring': primarySafe,
    ...(STATUS_BY_MODE[mode] || STATUS_BY_MODE.light),
    // Back-compat aliases for CSS already written against the old names.
    '--portal-primary': primarySafe,
    '--portal-on-primary': onPrimary,
    '--portal-accent': accentSafe,
  };
}
